import { useState } from "react";
import {
  buildPasswordResetRedirectUrl,
  requestPasswordResetEmail,
} from "@tennis-matches/services/auth";

/**
 * Shared forgot-password / request-reset-link flow for LoginPage and ResetPasswordPage.
 *
 * Unified vs. previous copies:
 * - busy only after empty-email check (ResetPassword timing)
 * - catch maps unexpected errors to `error` (LoginPage behaviour)
 * - own email state; LoginPage prefills via `prefillEmail` when opening forgot mode
 *
 * @param {{ t: (key: string, params?: object) => string }} options
 */
export function useForgotPasswordRequest({ t }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const clearMessages = () => {
    setError("");
    setInfo("");
  };

  const prefillEmail = (value) => {
    setEmail(
      String(value || "")
        .trim()
        .toLowerCase(),
    );
  };

  const onSubmit = async () => {
    clearMessages();
    const normalized = String(email || "")
      .trim()
      .toLowerCase();
    if (!normalized) {
      setError(t("auth.forgotPassword.error.missingEmail"));
      return;
    }
    setBusy(true);
    try {
      const result = await requestPasswordResetEmail(
        normalized,
        buildPasswordResetRedirectUrl(),
      );
      if (!result?.ok) {
        setError(
          result?.reason === "missing-email"
            ? t("auth.forgotPassword.error.missingEmail")
            : t("auth.forgotPassword.error.sendFailed"),
        );
        return;
      }
      setInfo(t("auth.forgotPassword.success"));
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return {
    email,
    setEmail,
    busy,
    error,
    info,
    setError,
    setInfo,
    clearMessages,
    prefillEmail,
    onSubmit,
  };
}
